import * as React from "react";
import { FormGroup } from "../RightAsideHelpers";
import { WorkflowNode } from "../../types";
import { TeamSelect } from "@/components/messaging/TeamSelect";
import Select from "@/components/Select";
import useUsers from "@/hooks/useUsers";
import { RadioLabel } from "@velaro/velaro-component-lib";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function TransferToAgentsEditor({ node, onUpdate }: Props) {
  const { userOptions } = useUsers();
  const specificTeam = !!node.specificTeam;
  const specificAgent = !!node.specificAgent;
  const hasRingTimeout = !!node.ringTimeoutSeconds && node.ringTimeoutSeconds > 0;

  return (
    <>
      <FormGroup
        label="Transfer to Agents on"
        tooltipText="Routing rules for the team will be followed unless assigning to a specific agent"
      >
        <div className="flex flex-col gap-2">
          <RadioLabel checked={!specificTeam}>
            <input
              type="radio"
              checked={!specificTeam}
              onChange={(e) => {
                onUpdate({ specificTeam: !e.currentTarget.checked });
              }}
            />
            <label className="pl-2">the current Team</label>
          </RadioLabel>
          <RadioLabel checked={specificTeam}>
            <input
              type="radio"
              checked={specificTeam}
              onChange={(e) => {
                onUpdate({ specificTeam: e.currentTarget.checked });
              }}
            />
            <label className="pl-2">a specific Team</label>
          </RadioLabel>
        </div>
      </FormGroup>
      {specificTeam && (
        <>
          <FormGroup label="Team">
            <TeamSelect
              value={node.teamId}
              onChange={(teamId) => {
                onUpdate({ teamId });
              }}
            />
          </FormGroup>
          <FormGroup>
            <div className="flex cursor-pointer">
              <input
                type="checkbox"
                className="cursor-pointer"
                id={`specificAgent`}
                checked={specificAgent}
                onChange={(e) => {
                  onUpdate({ specificAgent: e.currentTarget.checked });
                }}
              />
              <label htmlFor={`specificAgent`} className="pl-2 cursor-pointer">
                specific Agent
              </label>
            </div>
          </FormGroup>

          {specificAgent && (
            <FormGroup label="Agent">
              <Select
                options={userOptions}
                value={node.agentId}
                onChange={(agentId) => {
                  onUpdate({ agentId: Number(agentId) });
                }}
              />
            </FormGroup>
          )}
        </>
      )}

      <hr className="my-3 border-gray-100" />

      <FormGroup>
        <div className="flex cursor-pointer">
          <input
            type="checkbox"
            className="cursor-pointer"
            id="ringTimeout"
            checked={hasRingTimeout}
            onChange={(e) => {
              onUpdate({
                ringTimeoutSeconds: e.currentTarget.checked ? 30 : undefined,
                timeoutFallbackChildId: e.currentTarget.checked
                  ? node.timeoutFallbackChildId
                  : undefined
              });
            }}
          />
          <label htmlFor="ringTimeout" className="pl-2 cursor-pointer">
            Escalate if no agent accepts
          </label>
        </div>
      </FormGroup>

      {hasRingTimeout && (
        <FormGroup
          label="Ring time (seconds)"
          tooltipText="If no agent accepts within this many seconds, the workflow continues from the escalation output."
        >
          <input
            type="number"
            min={5}
            max={3600}
            className="input w-full"
            value={node.ringTimeoutSeconds ?? 30}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              onUpdate({ ringTimeoutSeconds: isNaN(val) || val < 5 ? 5 : val });
            }}
          />
          <p className="text-xs text-gray-400 mt-1">
            Connect the escalation output on this node to the next step (e.g. a Tier 2 team or a message).
          </p>
        </FormGroup>
      )}
    </>
  );
}
